var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createComponentQuestionSet, updateComponentQuestionSet, toggleComponentQuestionSetStatus, getComponentQuestionSets } from '../services/ComponentQuestionSetsService';
// Query Keys
export const COMPONENT_QUESTION_SETS_QUERY_KEYS = {
    all: ['componentQuestionSets'],
    list: () => ['componentQuestionSets', 'list']
};
// Hook to fetch component question sets
export function useComponentQuestionSets() {
    return useQuery({
        queryKey: COMPONENT_QUESTION_SETS_QUERY_KEYS.list(),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getComponentQuestionSets();
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data || [];
        }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3
    });
}
// Hook to create a component question set
export function useCreateComponentQuestionSetMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => __awaiter(this, void 0, void 0, function* () {
            const response = yield createComponentQuestionSet(data);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (newQuestionSet) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            // Cancel any outgoing refetches to avoid overwriting our optimistic update
            yield queryClient.cancelQueries({ queryKey: COMPONENT_QUESTION_SETS_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousQuestionSets = queryClient.getQueryData(COMPONENT_QUESTION_SETS_QUERY_KEYS.list());
            // Optimistically update to the new value with temporary ID
            if (previousQuestionSets) {
                const optimisticQuestionSet = {
                    id: Date.now(), // Temporary ID
                    name: newQuestionSet.name,
                    description: newQuestionSet.description || null,
                    active: (_a = newQuestionSet.active) !== null && _a !== void 0 ? _a : true,
                    questionsCount: 0,
                    componentTypesCount: 0,
                    editUrl: '',
                    deleteUrl: '',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
                queryClient.setQueryData(COMPONENT_QUESTION_SETS_QUERY_KEYS.list(), [
                    ...previousQuestionSets,
                    optimisticQuestionSet
                ]);
            }
            // Return a context object with the snapshotted value
            return { previousQuestionSets };
        }),
        onSuccess: () => {
            toast.success('Component question set created successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_QUESTION_SETS_QUERY_KEYS.all
            });
        },
        onError: (error, _newQuestionSet, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousQuestionSets) {
                queryClient.setQueryData(COMPONENT_QUESTION_SETS_QUERY_KEYS.list(), context.previousQuestionSets);
            }
            toast.error(error.message || 'Failed to create component question set. Please try again.');
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_QUESTION_SETS_QUERY_KEYS.all
            });
        }
    });
}
// Hook to update a component question set
export function useUpdateComponentQuestionSetMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ id, data }) {
            const response = yield updateComponentQuestionSet(id, data);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ id, data }) {
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries({ queryKey: COMPONENT_QUESTION_SETS_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousQuestionSets = queryClient.getQueryData(COMPONENT_QUESTION_SETS_QUERY_KEYS.list());
            // Optimistically update to the new value
            if (previousQuestionSets) {
                const updatedQuestionSets = previousQuestionSets.map((questionSet) => {
                    var _a, _b, _c;
                    return questionSet.id === id
                        ? Object.assign(Object.assign({}, questionSet), { name: (_a = data.name) !== null && _a !== void 0 ? _a : questionSet.name, description: (_b = data.description) !== null && _b !== void 0 ? _b : questionSet.description, active: (_c = data.active) !== null && _c !== void 0 ? _c : questionSet.active, updatedAt: new Date().toISOString() }) : questionSet;
                });
                queryClient.setQueryData(COMPONENT_QUESTION_SETS_QUERY_KEYS.list(), updatedQuestionSets);
            }
            // Return a context object with the snapshotted value
            return { previousQuestionSets };
        }),
        onSuccess: () => {
            toast.success('Component question set updated successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_QUESTION_SETS_QUERY_KEYS.all
            });
        },
        onError: (error, _variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousQuestionSets) {
                queryClient.setQueryData(COMPONENT_QUESTION_SETS_QUERY_KEYS.list(), context.previousQuestionSets);
            }
            toast.error(error.message || 'Failed to update component question set. Please try again.');
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_QUESTION_SETS_QUERY_KEYS.all
            });
        }
    });
}
// Hook to toggle component question set active status (replaces delete functionality)
export function useToggleComponentQuestionSetStatusMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => __awaiter(this, void 0, void 0, function* () {
            const response = yield toggleComponentQuestionSetStatus(id);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (toggledId) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries({ queryKey: COMPONENT_QUESTION_SETS_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousQuestionSets = queryClient.getQueryData(COMPONENT_QUESTION_SETS_QUERY_KEYS.list());
            // Optimistically toggle the active status
            if (previousQuestionSets) {
                const updatedQuestionSets = previousQuestionSets.map((questionSet) => questionSet.id === toggledId
                    ? Object.assign(Object.assign({}, questionSet), { active: !questionSet.active, updatedAt: new Date().toISOString() }) : questionSet);
                queryClient.setQueryData(COMPONENT_QUESTION_SETS_QUERY_KEYS.list(), updatedQuestionSets);
            }
            // Return a context object with the snapshotted value
            return { previousQuestionSets };
        }),
        onSuccess: (data) => {
            const status = (data === null || data === void 0 ? void 0 : data.active) ? 'activated' : 'deactivated';
            toast.success(`Component question set ${status} successfully!`);
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_QUESTION_SETS_QUERY_KEYS.all
            });
        },
        onError: (error, _toggledId, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousQuestionSets) {
                queryClient.setQueryData(COMPONENT_QUESTION_SETS_QUERY_KEYS.list(), context.previousQuestionSets);
            }
            toast.error(error.message || 'Failed to toggle component question set status. Please try again.');
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_QUESTION_SETS_QUERY_KEYS.all
            });
        }
    });
}
